import React from 'react';
import {Field, useFormikContext} from 'formik';
import {useCreateContactMutation} from '../../../../redux/deliveryApi';
import {handleApiCallWithFullError} from '../../../../assets/utils/utils';
import LightGreenButton from '../../../Buttons/LightGreenButton';
import SelectSvgSelector from '../../../InputSelect/SelectSvgSelector';
import style from '../../../../PersonalOffice/MyOrders/MyOrdersPopups/PopupsCommonStyle.module.css';
import styleCart from '../../../../PersonalOffice/Cart/Cart.module.css';
import {useTranslation} from 'react-i18next';

type Props = {
	supplier_id: string;
	setNewContact: (value: boolean) => void;
};

// Reusable field component
const ContactField: React.FC<{
	name: string;
	label: string;
	placeholder: string;
	icon: string;
	error?: string;
	touched?: boolean;
}> = ({name, label, placeholder, icon, error, touched}) => {
	const {getFieldProps, getFieldMeta} = useFormikContext();
	const fieldProps = getFieldProps(name);
	const meta = getFieldMeta(name);
	const hasError = (error || meta.error) && (touched || meta.touched);

	return (
		<div className={style.fieldElement}>
			<div className={style.fieldElement_Name}>{label}</div>
			<div
				className={`${style.fieldElement_Input} ${hasError ? style.error : ''}`}
			>
				<span className={style.fieldIcon}>
					<SelectSvgSelector id={icon} />
				</span>
				<Field
					className={style.input}
					placeholder={placeholder}
					{...fieldProps}
				/>
			</div>
			{hasError && (
				<div className={style.errorMessage}>{error || meta.error}</div>
			)}
		</div>
	);
};

const NewContactForm: React.FC<Props> = ({supplier_id, setNewContact}) => {
	const {t} = useTranslation();
	const {values, setFieldValue, setStatus, status} = useFormikContext<any>();
	const [createContact, {isLoading: isCreatingContact}] =
		useCreateContactMutation();

	const handleCreateContact = async () => {
		setStatus(null); // Clear previous errors
		const data = {
			supplier_id: supplier_id,
			phone: values.newContactPhone,
			surname: values.newContactSurname,
			name: values.newContactName,
			middlename: values.newContactMiddlename,
		};

		await handleApiCallWithFullError({
			apiCall: async () => createContact(data),
			onSuccess: (result) => {
				if (result.approve) {
					setFieldValue('contact_id', result.contact_id);
					setNewContact(false);
					// Clear form fields
					setFieldValue('newContactPhone', '');
					setFieldValue('newContactSurname', '');
					setFieldValue('newContactName', '');
					setFieldValue('newContactMiddlename', '');
				}
			},
			onValidationError: (fieldErrors) => {
				// Handle field-specific validation errors
				setStatus(fieldErrors);
			},
			onError: (error) => {
				// Handle general errors
				const errorMessage =
					error?.data?.message ||
					error.message ||
					t('Помилка створення контакту');
				setStatus({general: errorMessage});
			},
		});
	};

	return (
		<>
			<div className={style.editContainer}>
				<div className={style.editColumn}>
					<ContactField
						name="newContactPhone"
						label={t('Телефон')}
						placeholder={t('Телефон')}
						icon="phone"
						error={status?.phone}
						touched={status?.phone}
					/>
					<ContactField
						name="newContactSurname"
						label={t('Прізвище')}
						placeholder={t('Прізвище')}
						icon="nameIcon"
						error={status?.surname}
						touched={status?.surname}
					/>
				</div>
				<div className={style.editColumn}>
					<ContactField
						name="newContactName"
						label={t("Ім'я")}
						placeholder={t("Ім'я")}
						icon="nameIcon"
						error={status?.name}
						touched={status?.name}
					/>
					<ContactField
						name="newContactMiddlename"
						label={t('Побатькові')}
						placeholder={t('Побатькові')}
						icon="nameIcon"
						error={status?.middlename}
						touched={status?.middlename}
					/>
				</div>
			</div>

			{status?.general && (
				<div className={style.generalError}>{status.general}</div>
			)}

			<div className={styleCart.submitButtonWrapper}>
				<LightGreenButton
					type={'button'}
					onClick={handleCreateContact}
					text={isCreatingContact ? t('Створення...') : t('Продовжити')}
				/>
			</div>
		</>
	);
};

export default NewContactForm;
